import React from 'react';
import { Container } from 'react-bootstrap';
import Footer from './Footer';

const E_LearningPrivacy = () => {
  return (
    <div className="text-start bg-light pt-5">
      <Container className="bg-white p-5 rounded shadow" style={{ maxWidth: '900px' }}>
        <h2 className="text-center fw-bold text-primary mb-4 text-uppercase">Privacy Policy</h2>
        <p className="text-center fw-bold">
          <strong>Last Updated:</strong> October 22, 2025
        </p>

        <h5 className="border-start border-4 border-primary ps-3 fw-bold mt-5">Introduction</h5>
        <p>
          This Privacy Policy describes how we collect, use, and protect your information when you use our educational learning application (the <strong>"Cortexa"</strong>). We are committed to protecting your privacy and ensuring transparency about our data practices.
        </p>

        <h5 className="border-start border-4 border-primary ps-3 fw-bold mt-5">Information We Collect</h5>
        <h6 className="fw-bold mt-3">Personal Information</h6>
        <p>
          When you use our App, we collect the following information:
        </p>
        <ul>
          <li><strong>Email Address:</strong> Collected through Google Single Sign-On (SSO) authentication.</li>
          <li><strong>Name:</strong> Collected through Google Single Sign-On (SSO) authentication.</li>
        </ul>

        <h6 className="fw-bold mt-3">User-Generated Content</h6>
        <p>
          The App processes the following types of content you provide:
        </p>
        <ul>
          <li><strong>Educational Content:</strong> Text, images, or PDF files containing chapter content you wish to learn.</li>
          <li><strong>Voice Responses:</strong> Verbal answers you provide to questions (processed but not stored permanently).</li>
          <li><strong>Text Responses:</strong> Written answers to assessment questions.</li>
          <li><strong>Chat History:</strong> Records of your interactions with the App, including explanations and Q&A sessions.</li>
          <li><strong>Feedback Data:</strong> Likes, dislikes, and suggestions you provide about the App's responses.</li>
        </ul>

        <h6 className="fw-bold mt-3">Usage Information</h6>
        <ul>
          <li><strong>App Preferences:</strong> Your selected explanation level (brief, standard, or detailed) and language preferences.</li>
          <li><strong>Interaction Data:</strong> Records of how you use the App's features.</li>
        </ul>

        <h5 className="border-start border-4 border-primary ps-3 fw-bold mt-5">How We Use Your Information</h5>
        <p>
          We use the collected information for the following purposes:
        </p>
        <ul>
          <li>To verify your identity and provide secure access to the App (<strong>Authentication</strong>).</li>
          <li>To maintain your chat history and provide a personalized learning experience (<strong>Personalization</strong>).</li>
          <li>To analyze your educational materials and generate appropriate explanations, examples, and stories (<strong>Content Processing</strong>).</li>
          <li>To evaluate your answers and provide feedback (<strong>Assessment</strong>).</li>
          <li>To convert written responses into audio format in your selected language (<strong>Text-to-Speech</strong>).</li>
          <li>To understand user feedback and improve the App's educational content and features (<strong>Service Improvement</strong>).</li>
          <li>To maintain your user profile and preferences (<strong>Account Management</strong>).</li>
        </ul>

        <h5 className="border-start border-4 border-primary ps-3 fw-bold mt-5">Data Storage and Security</h5>
        <ul>
          <li><strong>Chat History:</strong> Your conversations are stored securely and associated with your account to enable access through the drawer/sidebar menu.</li>
          <li><strong>Security Measures:</strong> We implement industry-standard security measures to protect your personal information from unauthorized access, disclosure, or destruction.</li>
          <li><strong>Voice Data:</strong> Verbal responses are processed for evaluation but are not permanently stored.</li>
        </ul>

        <h5 className="border-start border-4 border-primary ps-3 fw-bold mt-5">Third-Party Services</h5>
        <h6 className="fw-bold mt-3">Google Sign-In</h6>
        <p>
          We use Google Single Sign-On for authentication. By signing in with Google, you agree to Google's privacy policy and terms of service. We only receive your email address and name from Google.
        </p>

        <h6 className="fw-bold mt-3">Text-to-Speech Services</h6>
        <p>
          The App uses text-to-speech technology to read responses aloud. This processing may be performed by third-party services, but no personal information is shared beyond the text content being converted to speech.
        </p>

        <h5 className="border-start border-4 border-primary ps-3 fw-bold mt-5">Data Sharing and Disclosure</h5>
        <p>
          We do not sell, trade, or rent your personal information to third parties. We may share information only in the following circumstances:
        </p>
        <ul>
          <li>With your explicit consent.</li>
          <li>When required by law, court order, or government regulation.</li>
          <li>To protect the rights, property, or safety of our App, users, or others.</li>
        </ul>

        <h5 className="border-start border-4 border-primary ps-3 fw-bold mt-5">Your Rights and Choices</h5>
        <p>
          You have the following rights regarding your data:
        </p>
        <ul>
          <li><strong>Access:</strong> View your chat history through the App's drawer/sidebar.</li>
          <li><strong>Deletion:</strong> Request deletion of your account and associated data by contacting us.</li>
          <li><strong>Data Portability:</strong> Request a copy of your data in a portable format.</li>
          <li><strong>Opt-Out:</strong> Stop using the App at any time, which will cease data collection.</li>
          <li><strong>Feedback Control:</strong> Choose whether to provide feedback on responses.</li>
        </ul>

        <h5 className="border-start border-4 border-primary ps-3 fw-bold mt-5">Children's Privacy</h5>
        <p>
          Our App is designed for educational purposes and may be used by students. We comply with applicable children's privacy laws, including COPPA (Children's Online Privacy Protection Act) where applicable. If you are under 13 (or the applicable age in your jurisdiction), please have a parent or guardian review this Privacy Policy and provide consent before using the App.
        </p>
        <p>
          We do not knowingly collect more information than necessary from users of any age. The only personal information we collect is your name and email address from Google SSO.
        </p>

        <h5 className="border-start border-4 border-primary ps-3 fw-bold mt-5">Data Retention</h5>
        <ul>
          <li><strong>Account Data:</strong> Retained as long as your account is active.</li>
          <li><strong>Chat History:</strong> Retained until you delete specific conversations or your account.</li>
          <li><strong>Feedback Data:</strong> Retained for service improvement purposes.</li>
          <li><strong>Deleted Data:</strong> When you delete your account, we will remove your personal information and chat history within 30 days.</li>
        </ul>

        <h5 className="border-start border-4 border-primary ps-3 fw-bold mt-5">International Data Transfers</h5>
        <p>
          Your information may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place to protect your data in accordance with this Privacy Policy.
        </p>

        <h5 className="border-start border-4 border-primary ps-3 fw-bold mt-5">Changes to This Privacy Policy</h5>
        <p>
          We may update this Privacy Policy from time to time. We will notify you of any material changes by:
        </p>
        <ul>
          <li>Posting the updated policy in the App.</li>
          <li>Updating the "Last Updated" date at the top of this policy.</li>
          <li>Sending an email notification for significant changes (if applicable).</li>
        </ul>
        <p>
          Your continued use of the App after changes are posted constitutes your acceptance of the updated Privacy Policy.
        </p>

        <h5 className="border-start border-4 border-primary ps-3 fw-bold mt-5">Contact Us</h5>
        <p>
          If you have questions, concerns, or requests regarding this Privacy Policy or your personal information, please contact us at:
        </p>
        <p>
          <strong>Email:</strong> <a href="mailto:info@warals.com" className="text-black text-decoration-none">info@warals.com</a><br />
          <strong>Address:</strong> Warals Technology
        </p>

        <h5 className="border-start border-4 border-primary ps-3 fw-bold mt-5">Consent</h5>
        <p>
          By using our App, you consent to the collection and use of information as described in this Privacy Policy.
        </p>

        <hr />
        <h5 className="border-start border-4 border-primary ps-3 fw-bold mt-5">Data Processing Summary for Google Play</h5>
        <h6 className="fw-bold mt-3">Data Collected:</h6>
        <ul>
          <li>Email address (for authentication and account management).</li>
          <li>Name (for personalization).</li>
          <li>User-generated content (educational materials, responses).</li>
          <li>Chat history (for user access to past interactions).</li>
          <li>Feedback data (for service improvement).</li>
        </ul>
        <h6 className="fw-bold mt-3">Data Usage:</h6>
        <ul>
          <li>Authentication and account management.</li>
          <li>Providing personalized educational content.</li>
          <li>Maintaining chat history for user reference.</li>
          <li>Service improvement based on feedback.</li>
        </ul>
        <h6 className="fw-bold mt-3">Data Sharing:</h6>
        <ul>
          <li>No data is sold to third parties.</li>
          <li>Google SSO provider receives authentication requests only.</li>
          <li>No personal information is shared with other third parties.</li>
          <li>Voice Responses: Verbal answers are processed in real-time to generate results, but are not stored, shared, or used for training any external models.</li>
        </ul>
        <h6 className="fw-bold mt-3">Security:</h6>
        <ul>
          <li>Industry-standard encryption and security measures.</li>
          <li>Secure storage of user data.</li>
          <li>Regular security audits and updates.</li>
        </ul>
        <h6 className="fw-bold mt-3">App Permissions:</h6>
        <p>
          The app may request access to:
        </p>
        <ul>
          <li><strong>Microphone:</strong> For recording voice responses during learning sessions.</li>
          <li><strong>Storage:</strong> To upload and access educational files (PDFs, images).</li>
        </ul>
        <p>
          The permissions are used only for their respective educational functions and are not used to collect personal data.
        </p>
      </Container>
      <div className=" pt-5">
      <Footer/></div>

         </div>
         
  );
};

export default E_LearningPrivacy;
